import { call, getContext, put, select, take, takeEvery } from 'redux-saga/effects';

import dynaForms, { dtoBuilder } from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import {
    actions as uiActions,
    selectors as uiSelectors,
    types as uiTypes,
} from '@elab/core/store/ui';
import logger from '@elab/core/utils/logger';
import { selectors as configSelectors } from '@elab/core/store/config';

import { loadPinCodeRequired } from '../api';

import getActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';

const DIALOG_NAME = 'pincode-form-popup';
const PIN_CODE_FIELD_NAME = 'pinCode';
const CARD_NUMBER_FIELD_NAME_BY_FORM_ID = {
    pinCodeForm: 'loyaltyOptionsForRegistrationForm.value.0.cardNumber',
    enrollmentPinCodeForm: 'cardNumber',
};
const ACTIVATION_CODE_FIELD_NAME_BY_FORM_ID = {
    pinCodeForm: 'loyaltyOptionsForRegistrationForm.value.0.activationCode',
    enrollmentPinCodeForm: 'dateOfBirth',
};
const PHONE_FIELD_NAME = 'phone';
const CARD_NUMBER_FIELD_NAME = 'cardNumber';
const ACTIVATION_CODE = 'activationCode';

export const checkPinCode = ({ selectors, actions, storeName }) => {
    const PARENT_CARD_NUMBER_NAME = CARD_NUMBER_FIELD_NAME_BY_FORM_ID[storeName];
    const PARENT_ACTIVATION_CODE_NAME = ACTIVATION_CODE_FIELD_NAME_BY_FORM_ID[storeName];
    return function*() {
        try {
            const cardNumber = yield select(selectors.parent.getDataField, PARENT_CARD_NUMBER_NAME);
            const activationCode = yield select(
                selectors.parent.getDataField,
                PARENT_ACTIVATION_CODE_NAME,
            );

            const dto = yield call(dtoBuilder, [cardNumber]);
            const shouldAskPinCode = yield call(loadPinCodeRequired, dto);
            if (!shouldAskPinCode) {
                yield put(actions.parent.setPinCodeStatus(true));
                return;
            }
            yield put(actions.setDataFieldValue(CARD_NUMBER_FIELD_NAME, cardNumber.value));
            const phone = yield select(selectors.parent.getDataFieldValue, PHONE_FIELD_NAME);
            yield put(actions.setDataFieldValue(PHONE_FIELD_NAME, phone));
            yield put(actions.setDataFieldValue(ACTIVATION_CODE, activationCode.value));
            const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
            if (isNewLoyaltyFlow) {
                if (storeName == 'enrollmentPinCodeForm') {
                    yield put(uiActions.openDialog({ name: DIALOG_NAME }));
                } else {
                    yield put(actions.parent.showPinCode(true));
                }
            } else {
                yield put(uiActions.openDialog({ name: DIALOG_NAME }));
            }
        } catch (e) {
            yield call(logger.error, e);
            yield put(actions.parent.setPinCodeStatus(true));
        }
    };
};

export function* runPinCodeChecks() {
    const registrationWithPinCode = yield select(configSelectors.registrationWithPinCode);
    if (!registrationWithPinCode) {
        return true;
    }

    const { actions, types } = yield getContext('ctx');
    yield put(actions.checkPinCode());
    const pinCodeStatus = yield take(types.SET_PIN_CODE_STATUS);
    return pinCodeStatus.payload;
}

export const handleDialogClosing = ({ actions }) =>
    function*() {
        const openedDialog = yield select(uiSelectors.getDialogName);
        if (openedDialog === DIALOG_NAME) {
            yield put(actions.parent.setPinCodeStatus(false));
        }
    };

export function* afterStepValidation({ selectors, actions }) {
    const pinCode = yield select(selectors.getDataFieldValue, PIN_CODE_FIELD_NAME);
    yield put(actions.parent.setDataFieldValue(PIN_CODE_FIELD_NAME, pinCode, true));
    yield put(actions.parent.setPinCodeStatus(true));
    yield put(uiActions.closeDialog({ name: DIALOG_NAME }));
    yield put(actions.setDataFieldValue(PIN_CODE_FIELD_NAME, null, true));
}

export function* onError({ payload }) {
    if (payload.name === CARD_NUMBER_FIELD_NAME && payload.error) {
        const { actions, storeName } = yield getContext('ctx');
        const name = CARD_NUMBER_FIELD_NAME_BY_FORM_ID[storeName];
        yield put(actions.parent.setScrollToField({ name, value: true }));
        yield put(actions.parent.setDataFieldError(name, payload.error));
    }
}

export default ({ storeName, parentStoreName }) => formId => {
    const actions = getActions({ storeName, parentStoreName });
    const selectors = getSelectors({ storeName, parentStoreName });
    const types = getTypes({ storeName, parentStoreName });
    const ctx = { storeName: formId || storeName, actions, selectors, types };
    return [
        ...dynaForms.createSagaFor(
            ctx,
            dynaFormsApi.getFormMetadata(formId || storeName),
            null,
            afterStepValidation,
        ),
        takeEvery(types.parent.CHECK_PIN_CODE, checkPinCode(ctx)),
        takeEvery(uiTypes.CLOSE_DIALOG_BY_NAME, handleDialogClosing(ctx)),
        takeEvery(types.SET_DATA_FIELD_ERROR, onError),
    ];
};
